/**
 * Metrics Dashboard Component
 * Key financial metrics cards
 */

import React from 'react';
import { FinancialForecast, ScenarioType } from '../../../types/financialForecasting';
import { formatCurrency } from './FinancialChart';

interface MetricsDashboardProps {
  forecast: FinancialForecast;
  activeScenario: ScenarioType;
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, trend, icon }) => {
  const trendColors = {
    up: 'text-green-600 bg-green-50',
    down: 'text-red-600 bg-red-50',
    neutral: 'text-gray-600 bg-gray-50'
  };

  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {icon && <span className="text-2xl">{icon}</span>}
      </div>
      <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
      {subtitle && (
        <p className="text-sm text-gray-500">{subtitle}</p>
      )}
      {trend && (
        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${trendColors[trend]}`}>
          <span>{trendIcons[trend]}</span>
          <span className="ml-1">{trend === 'up' ? 'Growing' : trend === 'down' ? 'Declining' : 'Stable'}</span>
        </div>
      )}
    </div>
  );
};

export const MetricsDashboard: React.FC<MetricsDashboardProps> = ({
  forecast,
  activeScenario
}) => {
  const periods = forecast.projections[activeScenario];
  const firstPeriod = periods[0];
  const lastPeriod = periods[periods.length - 1];

  // Calculate metrics
  const totalRevenue = periods.reduce((sum: number, p) => sum + p.totalRevenue, 0);
  const totalExpenses = periods.reduce((sum: number, p) => sum + p.totalExpenses, 0);
  const totalProfit = totalRevenue - totalExpenses;
  
  const avgMonthlyRevenue = totalRevenue / periods.length;
  const avgMonthlyExpenses = totalExpenses / periods.length;
  
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
  
  // Find break-even period
  const breakEvenPeriod = periods.findIndex((p) => p.cumulativeCashFlow >= 0);
  const breakEvenMonths = breakEvenPeriod !== -1 ? breakEvenPeriod + 1 : 'Not reached';

  // Revenue growth
  const revenueGrowth = firstPeriod.totalRevenue > 0 
    ? ((lastPeriod.totalRevenue - firstPeriod.totalRevenue) / firstPeriod.totalRevenue) * 100
    : 0;

  const currentAssets = lastPeriod.currentAssets ?? 0;
  const currentLiabilities = lastPeriod.currentLiabilities ?? 0;
  const cash = lastPeriod.cashBalance ?? lastPeriod.cumulativeCashFlow;
  const ar = lastPeriod.accountsReceivable ?? 0;

  const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : undefined;
  const quickRatio = currentLiabilities > 0 ? (cash + ar) / currentLiabilities : undefined;

  const ebitda = (lastPeriod.totalRevenue ?? 0) - (lastPeriod.totalExpenses ?? 0);
  const debtService = (lastPeriod.interestExpense ?? 0) + (lastPeriod.principalRepayment ?? 0);
  const dscr = debtService > 0 ? ebitda / debtService : undefined;

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(totalRevenue)}
          subtitle={`Avg: ${formatCurrency(avgMonthlyRevenue)}/period`}
          icon="💰"
          trend={revenueGrowth > 0 ? 'up' : revenueGrowth < 0 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="Total Expenses"
          value={formatCurrency(totalExpenses)}
          subtitle={`Avg: ${formatCurrency(avgMonthlyExpenses)}/period`}
          icon="📊"
        />
        <MetricCard
          title="Net Profit"
          value={formatCurrency(totalProfit)}
          subtitle={`Margin: ${profitMargin.toFixed(1)}%`}
          icon={totalProfit >= 0 ? '✅' : '⚠️'}
          trend={totalProfit > 0 ? 'up' : totalProfit < 0 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="Break-Even"
          value={typeof breakEvenMonths === 'number' ? `Month ${breakEvenMonths}` : breakEvenMonths}
          subtitle={`From start of forecast`}
          icon="🎯"
          trend={typeof breakEvenMonths === 'number' && breakEvenMonths <= 12 ? 'up' : 'neutral'}
        />
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <MetricCard
          title="Revenue Growth"
          value={`${revenueGrowth > 0 ? '+' : ''}${revenueGrowth.toFixed(1)}%`}
          subtitle="First to last period"
          trend={revenueGrowth > 20 ? 'up' : revenueGrowth < -10 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="Final Cash Position"
          value={formatCurrency(cash)}
          subtitle="End of forecast period"
          trend={cash > 100000 ? 'up' : cash < 0 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="Profit Margin"
          value={`${profitMargin.toFixed(1)}%`}
          subtitle="Overall profitability"
          trend={profitMargin > 20 ? 'up' : profitMargin < 0 ? 'down' : 'neutral'}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <MetricCard
          title="Current Ratio"
          value={currentRatio !== undefined ? currentRatio.toFixed(2) : '—'}
          subtitle="Current assets / current liabilities"
          trend={currentRatio !== undefined && currentRatio >= 1.25 ? 'up' : currentRatio !== undefined && currentRatio < 1 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="Quick Ratio"
          value={quickRatio !== undefined ? quickRatio.toFixed(2) : '—'}
          subtitle="(Cash + AR) / current liabilities"
          trend={quickRatio !== undefined && quickRatio >= 1 ? 'up' : quickRatio !== undefined && quickRatio < 0.75 ? 'down' : 'neutral'}
        />
        <MetricCard
          title="DSCR"
          value={dscr !== undefined ? dscr.toFixed(2) : '—'}
          subtitle="EBITDA / (Interest + Principal)"
          trend={dscr !== undefined && dscr >= 1.25 ? 'up' : dscr !== undefined && dscr < 1 ? 'down' : 'neutral'}
        />
      </div>
    </div>
  );
};

export default MetricsDashboard;
